import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Save, Upload, X, Image as ImageIcon } from 'lucide-react';
import { PAPER_BASE } from '../config';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

export default function PaperAdminServicePage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const fileRefs = useRef({});

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchServices = () => {
    setLoading(true);
    axios.get(`${API}/api/paperWork/services`)
      .then(res => setServices(res.data.services || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchServices(); }, []);

  const handleFieldChange = (id, field, value) => {
    setServices(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const handleImageChange = async (id, file) => {
    if (!file) return;
    setSaving(id);
    try {
      const formData = new FormData();
      formData.append('image', file);
      await axios.put(`${API}/api/paperWork/services/${id}`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      fetchServices();
    } catch {
      alert('이미지 업로드 중 오류가 발생했습니다.');
    } finally {
      setSaving(null);
    }
  };

  const handleSave = async (svc) => {
    setSaving(svc.id);
    try {
      const formData = new FormData();
      formData.append('title', svc.title);
      formData.append('icon', svc.icon || '');
      formData.append('description', svc.description || '');
      formData.append('sortOrder', svc.sortOrder || 0);
      await axios.put(`${API}/api/paperWork/services/${svc.id}`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      fetchServices();
    } catch {
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="w-8 h-8 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {/* Admin Nav */}
      <div className="flex items-center gap-4 mb-6 border-b border-white/10 pb-3 overflow-x-auto">
        <Link to={`${PAPER_BASE}/admin/members`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">회원 관리</Link>
        <Link to={`${PAPER_BASE}/admin/works`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">행사 관리</Link>
        <Link to={`${PAPER_BASE}/admin/services`} className="text-sm font-medium text-violet-400 border-b-2 border-violet-400 pb-1 whitespace-nowrap">서비스 관리</Link>
        <Link to={`${PAPER_BASE}/admin/partners`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">파트너 관리</Link>
        <Link to={`${PAPER_BASE}/admin/blogs`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">스토리 관리</Link>
      </div>

      <h1 className="text-2xl font-light text-white tracking-wide mb-8">서비스 관리</h1>
      <p className="text-white/30 text-sm mb-8">메인페이지 "What we do" 섹션에 표시되는 서비스 카드를 관리합니다.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {services.map(svc => (
          <div key={svc.id} className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
            {/* Image area */}
            <input
              type="file"
              accept="image/*"
              ref={el => fileRefs.current[svc.id] = el}
              className="hidden"
              onChange={(e) => handleImageChange(svc.id, e.target.files[0])}
            />
            <div
              onClick={() => fileRefs.current[svc.id]?.click()}
              className="aspect-[16/10] bg-[#1a1428] cursor-pointer relative group overflow-hidden"
            >
              {svc.imageUrl ? (
                <img src={svc.imageUrl} alt={svc.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-white/20">
                  <ImageIcon size={32} />
                  <span className="text-xs mt-2">클릭하여 이미지 업로드</span>
                </div>
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <Upload size={24} className="text-white" />
              </div>
            </div>

            {/* Form fields */}
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-[10px] tracking-[0.15em] text-white/40 mb-1 uppercase">제목</label>
                <input
                  type="text"
                  value={svc.title}
                  onChange={(e) => handleFieldChange(svc.id, 'title', e.target.value)}
                  className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.15em] text-white/40 mb-1 uppercase">설명</label>
                <input
                  type="text"
                  value={svc.description || ''}
                  onChange={(e) => handleFieldChange(svc.id, 'description', e.target.value)}
                  className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] tracking-[0.15em] text-white/40 mb-1 uppercase">아이콘</label>
                  <select
                    value={svc.icon || ''}
                    onChange={(e) => handleFieldChange(svc.id, 'icon', e.target.value)}
                    className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
                  >
                    <option value="Music">Music</option>
                    <option value="Disc3">Disc3</option>
                    <option value="MonitorPlay">MonitorPlay</option>
                    <option value="Package">Package</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.15em] text-white/40 mb-1 uppercase">정렬</label>
                  <input
                    type="number"
                    value={svc.sortOrder || 0}
                    onChange={(e) => handleFieldChange(svc.id, 'sortOrder', parseInt(e.target.value) || 0)}
                    className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
                  />
                </div>
              </div>
              <button
                onClick={() => handleSave(svc)}
                disabled={saving === svc.id}
                className="flex items-center gap-2 px-4 py-2 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
              >
                <Save size={14} />
                {saving === svc.id ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
